import type { FilterCondition, FilterConditionOutput } from '../types';

// ============================================================================
// GENERADORES Y FACTORIES
// ============================================================================

// 🎯 Utilidad para generar IDs únicos
export const generateId = (): string =>
  Math.random().toString(36).substring(2, 11);

// 🎯 Utilidad para crear condición inicial
export const createInitialCondition = (): FilterCondition => ({
  id: generateId(),
  column: '',
  action: '',
  value: '',
  logic: 'y' as const,
});

// ============================================================================
// PROCESAMIENTO DE CONDICIONES
// ============================================================================

// 🎯 Utilidad para filtrar condiciones válidas
export const getValidConditions = (
  conditions: FilterCondition[]
): FilterCondition[] =>
  conditions.filter((c) => {
    if (!c.column || !c.action) return false;

    // Para acciones "is_null" y "is_not_null", no se requiere value
    if (c.action === 'is_null' || c.action === 'is_not_null') {
      return true;
    }

    // Para el resto de acciones, se requiere value
    return Boolean(c.value);
  });

// 🎯 Utilidad para convertir a output format
export const conditionsToOutput = (
  conditions: FilterCondition[]
): FilterConditionOutput[] =>
  getValidConditions(conditions).map(({ column, action, value, valueLabel }) => {
    const output: FilterConditionOutput = {
      column,
      action,
    };

    // Solo incluir value si la acción no es "is_null" o "is_not_null"
    if (action !== 'is_null' && action !== 'is_not_null') {
      output.value = value;
    }

    if (valueLabel !== undefined) {
      output.valueLabel = valueLabel;
    }

    return output;
  });

// 🆕 Utilidad para convertir condiciones de output a formato interno
export const outputToConditions = (
  outputConditions: FilterConditionOutput[]
): FilterCondition[] => {
  if (!outputConditions || outputConditions.length === 0) {
    return [createInitialCondition()];
  }

  return outputConditions.map((output, index) => {
    const normalizedValue =
      Array.isArray(output.value) ? output.value.join(',') : output.value ?? '';

    return {
      id: generateId(),
      column: output.column,
      action: output.action,
      value: normalizedValue,
      // Primera condición siempre 'y', las demás también por defecto (por ahora sólo soportamos 'y')
      logic: index === 0 ? ('y' as const) : ('y' as const),
      valueLabel: output.valueLabel,
    };
  });
};

// ============================================================================
// VALIDACIONES
// ============================================================================

// 🎯 Validar si una condición está completa
export const isConditionComplete = (condition: FilterCondition): boolean => {
  if (!condition.column || !condition.action) return false;

  // Para acciones "is_null" y "is_not_null", no se requiere value
  if (condition.action === 'is_null' || condition.action === 'is_not_null') {
    return true;
  }

  // Para el resto de acciones, se requiere value
  return Boolean(condition.value);
};

// 🎯 Validar si hay condiciones duplicadas
export const hasDuplicateConditions = (
  conditions: FilterCondition[]
): boolean => {
  const seen = new Set<string>();
  return conditions.some((c) => {
    if (!isConditionComplete(c)) return false;

    // Para acciones "is_null" y "is_not_null", no incluir value en la clave
    const key =
      c.action === 'is_null' || c.action === 'is_not_null'
        ? `${c.column}-${c.action}`
        : `${c.column}-${c.action}-${c.value}`;

    if (seen.has(key)) return true;
    seen.add(key);
    return false;
  });
};
